import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Sparkles, Plus, Trash2, Share2, Save, Download, Flower2, Loader2, Check, Lock,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { startLogin } from "@/components/Navbar";
import QuoteCheckout from "@/components/QuoteCheckout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";

const PLACEHOLDER =
  "https://images.unsplash.com/photo-1604378178297-1e0ddb643d7a?auto=format&fit=crop&w=900&q=80";

export default function Builder() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [selections, setSelections] = useState([]); // {flower_id, color_id}
  const [sizeId, setSizeId] = useState("standard");
  const [wrapId, setWrapId] = useState("satin");
  const [name, setName] = useState("My Wedding Bouquet");
  const [activeFlower, setActiveFlower] = useState(null);

  const [unlockedPrice, setUnlockedPrice] = useState(null);
  const [image, setImage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [config, setConfig] = useState(null);
  const [showQuote, setShowQuote] = useState(false);

  useEffect(() => {
    api.get("/catalog").then((r) => {
      setCatalog(r.data);
      setActiveFlower(r.data.flowers[0].id);
    });
    api.get("/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const spec = { flowers: selections, size_id: sizeId, wrap_id: wrapId, name };

  const colorMap = catalog ? Object.fromEntries(catalog.colors.map((c) => [c.id, c])) : {};
  const flowerMap = catalog ? Object.fromEntries(catalog.flowers.map((f) => [f.id, f])) : {};

  const addSelection = (colorId) => {
    if (!activeFlower) return;
    setSelections((prev) => {
      const exists = prev.find((s) => s.flower_id === activeFlower && s.color_id === colorId);
      if (exists) return prev;
      return [...prev, { flower_id: activeFlower, color_id: colorId }];
    });
    setSaved(null);
    setUnlockedPrice(null);
  };

  const removeSelection = (idx) => {
    setSelections((prev) => prev.filter((_, i) => i !== idx));
    setSaved(null);
    setUnlockedPrice(null);
  };

  const handleGenerate = async () => {
    if (selections.length === 0) {
      toast.error("Add at least one flower to your bouquet first.");
      return;
    }
    setGenerating(true);
    setSaved(null);
    try {
      const r = await api.post("/generate", spec);
      setImage(r.data.image);
      toast.success("Your bouquet is ready! 💐");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!user) { startLogin(); return; }
    if (!image) { toast.error("Generate your bouquet before saving."); return; }
    setSaving(true);
    try {
      const r = await api.post("/bouquets", { spec, image, name });
      setSaved(r.data);
      toast.success("Saved to My Bouquets.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    let record = saved;
    if (!record) {
      if (!user) { startLogin(); return; }
      if (!image) { toast.error("Generate your bouquet first."); return; }
      try {
        const r = await api.post("/bouquets", { spec, image, name });
        record = r.data;
        setSaved(r.data);
      } catch { toast.error("Could not create share link."); return; }
    }
    const url = `${window.location.origin}/b/${record.share_id}`;
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied to clipboard!");
  };

  const handleDownload = () => {
    if (!image) return;
    const a = document.createElement("a");
    a.href = image;
    a.download = `${name.replace(/\s+/g, "-").toLowerCase()}.png`;
    a.click();
  };

  if (!catalog) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="grid grid-cols-1 lg:grid-cols-12 lg:h-[calc(100vh-72px)] lg:overflow-hidden">
        {/* Controls */}
        <div className="lg:col-span-5 lg:h-full lg:overflow-y-auto custom-scroll bg-white border-r border-border p-6 md:p-8">
          <div className="mb-8">
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Bouquet name</label>
            <Input
              data-testid="bouquet-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 border-0 border-b border-border rounded-none px-0 text-2xl font-serif-display focus-visible:ring-0 focus-visible:border-primary"
            />
          </div>

          {/* Flowers */}
          <SectionTitle n="01" title="Choose a flower" />
          <div className="grid grid-cols-2 gap-3 mb-8">
            {catalog.flowers.map((f) => (
              <button
                key={f.id}
                data-testid={`flower-select-${f.id}`}
                onClick={() => setActiveFlower(f.id)}
                className={`text-left rounded-2xl border p-4 transition-all ${
                  activeFlower === f.id
                    ? "border-primary ring-2 ring-primary ring-offset-2 bg-primary/5"
                    : "border-border hover:border-secondary"
                }`}
              >
                <div className="text-2xl mb-1">{f.emoji}</div>
                <div className="font-medium text-sm">{f.name}</div>
                <div className="text-xs text-muted-foreground">${f.price.toFixed(2)}/stem</div>
              </button>
            ))}
          </div>

          {/* Colors */}
          <SectionTitle n="02" title={`Pick a colour for ${flowerMap[activeFlower]?.name || ""}`} />
          <div className="flex flex-wrap gap-3 mb-8">
            {catalog.colors.map((c) => (
              <button
                key={c.id}
                data-testid={`color-select-${c.id}`}
                onClick={() => addSelection(c.id)}
                title={c.name}
                className="group relative w-10 h-10 rounded-full border border-border shadow-sm hover:scale-110 transition-transform"
                style={{ backgroundColor: c.hex }}
              >
                <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 whitespace-nowrap">
                  {c.name}
                </span>
                <Plus className="w-4 h-4 mx-auto opacity-0 group-hover:opacity-100" style={{ color: ["white", "ivory", "yellow", "peach", "blush"].includes(c.id) ? "#2B2D28" : "#fff" }} />
              </button>
            ))}
          </div>

          {/* Selected */}
          <SectionTitle n="03" title="Your blooms" />
          <div className="space-y-2 mb-8" data-testid="selected-list">
            {selections.length === 0 && (
              <p className="text-sm text-muted-foreground italic">No flowers added yet — tap a colour above.</p>
            )}
            {selections.map((s, i) => (
              <motion.div
                key={`${s.flower_id}-${s.color_id}-${i}`}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center justify-between bg-muted rounded-xl px-4 py-2.5"
              >
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full border border-border" style={{ backgroundColor: colorMap[s.color_id]?.hex }} />
                  <span className="text-sm font-medium">
                    {colorMap[s.color_id]?.name} {flowerMap[s.flower_id]?.name}
                  </span>
                </div>
                <button data-testid={`remove-selection-${i}`} onClick={() => removeSelection(i)} className="text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </motion.div>
            ))}
          </div>

          {/* Size */}
          <SectionTitle n="04" title="Bouquet size" />
          <div className="grid grid-cols-2 gap-3 mb-8">
            {catalog.sizes.map((s) => (
              <button
                key={s.id}
                data-testid={`size-select-${s.id}`}
                onClick={() => { setSizeId(s.id); setSaved(null); setUnlockedPrice(null); }}
                className={`text-left rounded-2xl border p-4 transition-all ${
                  sizeId === s.id ? "border-primary ring-2 ring-primary ring-offset-2 bg-primary/5" : "border-border hover:border-secondary"
                }`}
              >
                <div className="font-medium text-sm">{s.name}</div>
                <div className="text-xs text-muted-foreground">{s.desc}</div>
              </button>
            ))}
          </div>

          {/* Wrap */}
          <SectionTitle n="05" title="Wrapping & ribbon" />
          <div className="flex flex-wrap gap-2 mb-4">
            {catalog.wraps.map((w) => (
              <button
                key={w.id}
                data-testid={`wrap-select-${w.id}`}
                onClick={() => { setWrapId(w.id); setSaved(null); setUnlockedPrice(null); }}
                className={`rounded-full px-4 py-2 text-sm border transition-all ${
                  wrapId === w.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-secondary"
                }`}
              >
                {w.name}{w.price > 0 ? ` +$${w.price}` : ""}
              </button>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className="lg:col-span-7 lg:h-full relative bg-background flex items-center justify-center p-6 md:p-10">
          <div className="relative w-full max-w-lg">
            <div className="relative aspect-square rounded-[2rem] overflow-hidden border border-border shadow-lg bg-white">
              <AnimatePresence mode="wait">
                {generating ? (
                  <motion.div key="loading" className="absolute inset-0 flex flex-col items-center justify-center gap-4 shimmer">
                    <Flower2 className="w-10 h-10 text-primary animate-float" />
                    <p className="text-sm tracking-widest uppercase text-primary/80">Arranging your bouquet…</p>
                  </motion.div>
                ) : (
                  <motion.img
                    key={image || "placeholder"}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    src={image || PLACEHOLDER}
                    alt="Bouquet preview"
                    data-testid="bouquet-preview-image"
                    className={`w-full h-full object-cover ${image ? "" : "opacity-60"}`}
                  />
                )}
              </AnimatePresence>
              {!image && !generating && (
                <div className="absolute inset-0 flex items-end justify-center pb-8 pointer-events-none">
                  <p className="text-sm bg-white/80 backdrop-blur px-4 py-2 rounded-full text-muted-foreground">
                    Preview — generate to see your design
                  </p>
                </div>
              )}
            </div>

            {/* Price — locked until paid */}
            {selections.length > 0 && (
              unlockedPrice ? (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-5 backdrop-blur-xl bg-white/70 border border-white/40 shadow-[0_8px_32px_rgba(0,0,0,0.08)] rounded-2xl p-5"
                  data-testid="price-panel"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Your bouquet price</span>
                    <span className="font-serif-display text-3xl text-primary" data-testid="price-total">${unlockedPrice.total.toFixed(2)}</span>
                  </div>
                  <div className="space-y-1 max-h-28 overflow-y-auto custom-scroll">
                    {unlockedPrice.lines.map((l, i) => (
                      <div key={i} className="flex justify-between text-sm text-muted-foreground">
                        <span>{l.label}</span>
                        <span>${l.amount.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-5 backdrop-blur-xl bg-white/70 border border-white/40 shadow-[0_8px_32px_rgba(0,0,0,0.08)] rounded-2xl p-5"
                  data-testid="price-locked-panel"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Florist quote</span>
                    <span className="font-serif-display text-3xl text-muted-foreground/60 flex items-center gap-1.5">
                      <Lock className="w-5 h-5" /> $•••
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Unlock your bouquet's full price &amp; an obligation-free quote from {config?.florist_name || "our florist"} — no commitment.
                  </p>
                  <button
                    onClick={() => setShowQuote(true)}
                    data-testid="get-quote-btn"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium bg-secondary text-white hover:bg-[#C29363] transition-all"
                  >
                    <Lock className="w-4 h-4" />
                    Unlock price{config?.quote_fee ? ` — $${config.quote_fee.toFixed(2)}` : ""}
                  </button>
                </motion.div>
              )
            )}

            {/* Actions */}
            <div className="mt-5 flex flex-wrap gap-3">
              <button
                onClick={handleGenerate}
                disabled={generating}
                data-testid="generate-ai-btn"
                className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-medium bg-primary text-primary-foreground hover:bg-[#2C4631] transition-all shadow-sm hover:shadow-md disabled:opacity-60"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {image ? "Regenerate" : "Generate AI Bouquet"}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !image}
                data-testid="save-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-3.5 font-medium border border-primary text-primary hover:bg-primary/5 transition-all disabled:opacity-40"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                {saved ? "Saved" : "Save"}
              </button>
              <button
                onClick={handleShare}
                disabled={!image}
                data-testid="share-link-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full w-12 h-12 border border-border hover:border-secondary transition-all disabled:opacity-40"
                title="Copy share link"
              >
                <Share2 className="w-4 h-4" />
              </button>
              <button
                onClick={handleDownload}
                disabled={!image}
                data-testid="download-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full w-12 h-12 border border-border hover:border-secondary transition-all disabled:opacity-40"
                title="Download image"
              >
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <QuoteCheckout
        open={showQuote}
        onOpenChange={setShowQuote}
        config={config}
        bouquetName={name}
        shareId={saved?.share_id}
        spec={spec}
        onPaid={(p) => setUnlockedPrice(p)}
      />
    </div>
  );
}

function SectionTitle({ n, title }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="text-xs font-bold text-secondary">{n}</span>
      <h3 className="font-serif-display text-2xl">{title}</h3>
    </div>
  );
}
